import React, { useEffect, useState } from 'react';
import { Badge, Card, EmptyState, Metric, Pagination, SearchLg, Table } from '@opendate-app/setlist';
import { ToastContainer } from 'react-toastify';
import ReportingPageLayout from './ReportingPageLayout';
import LedgerLineItemsExportButton from './LedgerLineItemsExportButton';
import useReportingPage from '../hooks/useReportingPage';
import { currencyFormatter } from '../utils/reportingUtils';
import { formatLedgerDay, ledgerReportHref } from '../utils/ledgerLineItems';

const axios = require('axios').default;
const qs = require('qs');

const noop = () => {};
const PER_PAGE = 25;

const money = (value) => currencyFormatter.format(value || 0);

const nowrap = (content) => <span style={{ whiteSpace: 'nowrap' }}>{content}</span>;

const COLUMNS = [
  { key: 'posting_date', header: 'Posted', primary: true, render: (row) => nowrap(formatLedgerDay(row.posting_date)) },
  {
    key: 'posting_type',
    header: 'Type',
    render: (row) => <Badge size="sm" color={row.posting_type === 'refund' ? 'error' : 'success'}>{row.posting_type === 'refund' ? 'Refund' : 'Sale'}</Badge>,
  },
  { key: 'order_id', header: 'Order', render: (row) => row.order_id || '—' },
  { key: 'ticket_id', header: 'Ticket', render: (row) => row.ticket_id },
  { key: 'barcode', header: 'Barcode', render: (row) => row.barcode || '—' },
  { key: 'origin', header: 'Origin', render: (row) => (row.imported ? 'Imported' : 'Opendate') },
  { key: 'source_order_id', header: 'Source Order', render: (row) => row.source_order_id || '—' },
  { key: 'source_ticket_id', header: 'Source Ticket', render: (row) => row.source_ticket_id || '—' },
  { key: 'import_run_id', header: 'Import Run', render: (row) => row.import_run_id || '—' },
  { key: 'event_name', header: 'Event', render: (row) => nowrap(row.event_name || '—') },
  { key: 'event_date', header: 'Event Date', render: (row) => nowrap(formatLedgerDay(row.event_date)) },
  { key: 'venue_name', header: 'Venue', render: (row) => nowrap(row.venue_name || '—') },
  { key: 'buyer_name', header: 'Buyer', render: (row) => nowrap(row.buyer_name || '—') },
  { key: 'ticket_type_name', header: 'Ticket Type', render: (row) => nowrap(row.ticket_type_name || '—') },
  { key: 'ticket_state', header: 'State', render: (row) => row.ticket_state },
  { key: 'net_ticket_price', header: 'Net Price', align: 'right', render: (row) => money(row.net_ticket_price) },
  { key: 'fees', header: 'Fees', align: 'right', render: (row) => money(row.fees) },
  { key: 'taxes', header: 'Taxes', align: 'right', render: (row) => money(row.taxes) },
  { key: 'posting_total', header: 'Posting Total', align: 'right', render: (row) => money(row.posting_total) },
  { key: 'selected_amount', header: 'Selected Amount', align: 'right', render: (row) => money(row.selected_amount) },
  { key: 'all_in_total', header: 'All-in Total', align: 'right', render: (row) => money(row.all_in_total) },
];

const subtotal = (name, rows) => ({ name, total: true, amount: rows.reduce((sum, row) => sum + row.amount, 0) });

const breakdownRows = ({ ticket_revenue: ticketRevenue = [], fees = [], taxes = [] }) => [
  ...ticketRevenue,
  ...(ticketRevenue.length ? [subtotal('Total ticket revenue', ticketRevenue)] : []),
  ...fees,
  ...(fees.length ? [subtotal('Total fees', fees)] : []),
  ...taxes,
  ...(taxes.length ? [subtotal('Total taxes', taxes)] : []),
];

const bold = (row, content) => (row.total ? <strong>{content}</strong> : content);

const BREAKDOWN_COLUMNS = [
  { key: 'name', header: 'Item', primary: true, render: (row) => bold(row, nowrap(row.name)) },
  { key: 'rate', header: 'Rate', align: 'right', render: (row) => (row.rate ? `${row.rate}%` : '') },
  { key: 'general_ledger_code', header: 'GL Code', render: (row) => row.general_ledger_code || '' },
  { key: 'account', header: 'Account', render: (row) => row.account || '' },
  { key: 'amount', header: 'Amount', align: 'right', render: (row) => bold(row, nowrap(money(row.amount))) },
];

const breakdownRowKey = (row) => [row.name, row.rate, row.general_ledger_code].join('|');

function ReportingFinancesLedgerLineItemsPage({
  team, csrfToken, title, filters, filterLines, sidebarKey, backHref, backLabel,
}) {
  const { isMobile, sidebarOpen, setSidebarOpen, dateRange, setDateRange } = useReportingPage();

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [breakdown, setBreakdown] = useState({ ticket_revenue: [], fees: [], taxes: [] });
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!team?.id) return undefined;
    let ignore = false;
    setLoading(true);
    setError(null);
    axios.get(`/teams/${team.id}/reporting/finances/general-ledger/line-items/data.json`, {
      params: { ...filters, page, per_page: PER_PAGE },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
    })
      .then((response) => {
        if (ignore) return;
        setRows(response.data.line_items || []);
        setKpis(response.data.kpis || null);
        if (response.data.breakdown) setBreakdown(response.data.breakdown);
        setPagination(response.data.pagination || null);
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error('Failed to load ledger line items', requestError);
        setError('Failed to load line items.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => { ignore = true; };
  }, [team?.id, filters, page]);

  const kpiCards = [
    { label: 'Selected Amount', value: kpis && money(kpis.amount) },
    { label: 'Posting Total', value: kpis && money(kpis.posting_total) },
    { label: 'Line Items', value: kpis?.line_items.toLocaleString() },
  ];

  return (
    <ReportingPageLayout
      team={team}
      activeKey={sidebarKey}
      title={title}
      subtitle="Every ticket sale and refund behind this figure. Selected Amount is the part of each line that posts to the row you opened, and its total matches that row."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showDatePicker={false}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={(
        <div className="ml-auto">
          <LedgerLineItemsExportButton team={team} csrfToken={csrfToken} params={filters} label="Export CSV" />
        </div>
      )}
    >
      <a href={ledgerReportHref(backHref, filters)} className="d-inline-flex align-items-center mb-3" style={{ fontSize: 13, fontWeight: 500, color: 'var(--sl-fg-brand, #1982c4)', textDecoration: 'none' }}>
        <i className="fas fa-arrow-left mr-2" style={{ fontSize: 12 }} />
        Back to {backLabel}
      </a>

      <div className="mb-3" style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)' }}>
        {(filterLines || []).map(([label, value]) => (
          <div key={label}><strong>{label}:</strong> {value}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 16, marginBottom: 16 }}>
        {kpiCards.map((card) => <Metric key={card.label} label={card.label} value={card.value ?? '—'} />)}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Card padding="none">
          <div style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>Revenue, Fees &amp; Taxes by GL Code</div>
          <Table
            stickyFirstColumn
            columns={BREAKDOWN_COLUMNS}
            data={breakdownRows(breakdown)}
            getRowKey={breakdownRowKey}
            loading={loading && page === 1}
            empty={<EmptyState size="sm" icon={SearchLg} title="Nothing posted in this range." />}
          />
        </Card>
      </div>

      <Card padding="none">
        <Table
          stickyFirstColumn
          columns={COLUMNS}
          data={rows}
          getRowKey={(row) => row.id}
          loading={loading}
          error={error}
          empty={<EmptyState size="sm" icon={SearchLg} title="No line items in this range." />}
        />
        {pagination?.total_pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--sl-border-secondary, #e4e7ec)' }}>
            <Pagination currentPage={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
      <ToastContainer />
    </ReportingPageLayout>
  );
}

export default ReportingFinancesLedgerLineItemsPage;
